"use client";
import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Avatar,
  IconButton,
  Collapse,
  Pagination,
  Paper,
  Fade,
  Divider,
  Stack,
  TextField,
  Button
} from "@mui/material";
import { ExpandMore, ExpandLess, Send } from "@mui/icons-material";
import dayjs from "dayjs";
import {
  getUserQARequestsWithReplies,
  addThreadedReplyToQA
} from "../../api/userApi";
import useThemeColor from "../../utils/useTheme";
import getInitials from "../../utils/getInitials";
import { convertTextToOnlyLink } from "../../utils/getUrlFromText";
import { useSelector } from "react-redux";
import useSignedUrl from "../../utils/useSignedUrl";
import { useNavigate } from "react-router-dom";

const PastRequestsTable = ({ isViewingUserMeals, setIsViewingUserMeals }) => {
  const styles = useThemeColor();
  const currentUser = useSelector((state) => state.auth.user);
  const [page, setPage] = useState(1);
  const [requests, setRequests] = useState([]);
  const [totalRequests, setTotalRequests] = useState(0);
  const [rowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [replyTexts, setReplyTexts] = useState({});
  const [submitting, setSubmitting] = useState({});
  const navigate = useNavigate();
  const imgSrc = useSignedUrl(
    currentUser?.profilePic,
    process.env.REACT_APP_BACKEND_PROFILE_IMAGE_LOCATION
  );

  const ecImgSrc = useSignedUrl(
    "EC.jpg",
    process.env.REACT_APP_BACKEND_PROFILE_IMAGE_LOCATION
  );
  useEffect(() => {
    fetchRequests();
  }, [page]);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const data = await getUserQARequestsWithReplies({
        page: page - 1,
        limit: rowsPerPage
      });
      setRequests(data.requests || []);
      setTotalRequests(data.totalRequests || 0);
    } catch (error) {
      console.error("Error fetching requests:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (id) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleReplyChange = (id, value) => {
    setReplyTexts((prev) => ({ ...prev, [id]: value }));
  };

  const handleSubmitReply = async (requestId) => {
    const message = replyTexts[requestId]?.trim();
    if (!message) return;

    try {
      setSubmitting((prev) => ({ ...prev, [requestId]: true }));
      await addThreadedReplyToQA(requestId, { message });
      setReplyTexts((prev) => ({ ...prev, [requestId]: "" }));
      await fetchRequests();
    } catch (error) {
      console.error("Error submitting reply:", error);
    } finally {
      setSubmitting((prev) => ({ ...prev, [requestId]: false }));
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        bgcolor: styles.background,
        py: 3,
        px: { xs: 1.5, sm: 2, md: 4 }
      }}
    >


      {loading && requests.length === 0 ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress sx={{ color: styles.primaryColor }} />
        </Box>
      ) : requests.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 8, color: styles.modLabel }}>
          <Typography variant="body1">No answered questions yet.</Typography>
        </Box>
      ) : (
        <>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {requests.map((req) => (
              <Fade in key={req._id}>
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 2, sm: 3 },
                    borderRadius: 3,
                    bgcolor: styles.lightGreyColor,
                    border: `1px solid ${styles.trackerBackground}`,
                    transition: "0.3s",
                    "&:hover": {
                      transform: { xs: "none", sm: "translateY(-3px)" },
                      boxShadow: `0 6px 14px ${styles.trackerBackground}`
                    }
                  }}
                >
                  <Box
                    // onClick={() => handleToggle(req._id)}
                    sx={{
                      cursor: "pointer",
                      width: "100%"
                    }}
                  >
                    {/* ROW: Avatar + Name + Date */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%"
                      }}
                    >
                      {/* Avatar */}
                      {req.userId?.profilePic ? (
                        <Avatar
                          src={req.userId.profilePic}
                          sx={{ width: 38, height: 38 }}
                        />
                      ) : (
                        <Avatar sx={{ width: 38, height: 38 }}>
                          {getInitials(
                            req.userId?.displayName || req.userId?.fullName
                          )}
                        </Avatar>
                      )}

                      {/* Name + Date (same row) */}
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          justifyContent: "space-between",
                          alignItems: "center",
                          flex: 1,
                          ml: 1.3
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: 600,
                            color: styles.primaryColor,
                            fontSize: "0.95rem",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            maxWidth: "70%",
                            cursor: "pointer"
                          }}
                          onClick={() => {
                            if (req.userId?._id) {
                              setIsViewingUserMeals({
                                createdByUser: req.userId
                              });
                              navigate("/meals?isFromQA=true");
                            }
                          }}
                        >
                          {req.userId?.displayName ||
                            req.userId?.fullName ||
                            "EC's Answer"}
                        </Typography>

                        <Typography
                          variant="caption"
                          sx={{ color: styles.modLabel }}
                        >
                          {dayjs.utc(req.createdAt).format("MMM DD, YYYY")}
                        </Typography>
                      </Box>

                      {/* Expand Icon */}
                      {/* <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggle(req._id);
                        }}
                        sx={{ color: styles.color, ml: 1 }}
                      >
                        {expanded[req._id] ? <ExpandLess /> : <ExpandMore />}
                      </IconButton> */}
                    </Box>

                    {/* QUESTION BELOW — FULL WIDTH */}
                    <Typography
                      variant="body1"
                      sx={{
                        mt: 1.2,
                        color: styles.color,
                        fontWeight: 500,
                        lineHeight: 1.5,
                        whiteSpace: "pre-wrap",
                        overflowWrap: "break-word"
                      }}
                    >
                      {convertTextToOnlyLink(req.question) || req.question}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      mt: 2,
                      display: "flex",
                      justifyContent: "center"
                    }}
                  >
                    <Button
                      onClick={() => handleToggle(req._id)}
                      size="small"
                      endIcon={
                        expanded[req._id] ? (
                          <ExpandLess sx={{ fontSize: "13px", margin: 0 }} />
                        ) : (
                          <ExpandMore sx={{ fontSize: "13px", margin: 0 }} />
                        )
                      }
                      sx={{
                        fontSize: "13px",
                        gap: "2px",
                        padding: "2px 14px",
                        textTransform: "none",
                        fontWeight: 600,
                        color: styles.primaryColor,
                        display: "flex",
                        alignItems: "center",

                        "& .MuiButton-endIcon": {
                          margin: 0
                        }
                      }}
                    >
                      {expanded[req._id] ? "Hide Answer" : "See Answer"}
                    </Button>
                  </Box>

                  <Collapse in={expanded[req._id]} timeout="auto" unmountOnExit>
                    <Divider
                      sx={{ my: 2, borderColor: styles.trackerBackground }}
                    />

                    {/* Admin Answer */}
                    <Box
                      sx={{
                        width: "100%",
                        p: 2,
                        mb: 1.5,
                        wordBreak: "break-word",
                        bgcolor: styles.background,
                        borderRadius: 2,
                        border: `1px solid ${styles.trackerBackground}`
                      }}
                    >
                      {/* ROW: Avatar + Name (Title) */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          width: "100%"
                        }}
                      >
                        {/* Avatar */}
                        <Avatar
                          sx={{
                            width: 36,
                            height: 36,
                            bgcolor: styles.primaryColor,
                            fontSize: "0.9rem"
                          }}
                          src={ecImgSrc}
                        >
                          EC
                        </Avatar>

                        {/* Name (EC's Answer) */}
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "row",
                            justifyContent: "space-between",
                            alignItems: "center",
                            flex: 1,
                            ml: 2
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            sx={{
                              fontWeight: 600,
                              color: styles.primaryColor
                            }}
                          >
                            EC's Answer
                          </Typography>
                        </Box>
                        <Typography
                          variant="caption"
                          sx={{ color: styles.modLabel }}
                        >
                          {dayjs.utc(req.repliedAt).format("MMM DD, YYYY")}
                        </Typography>
                      </Box>

                      {/* ANSWER TEXT BELOW — FULL WIDTH */}
                      <Typography
                        variant="body1"
                        sx={{
                          mt: 1,
                          color: styles.color,
                          fontWeight: 500,
                          lineHeight: 1.5,
                          whiteSpace: "pre-wrap",
                          overflowWrap: "break-word"
                        }}
                      >
                        {convertTextToOnlyLink(req.reply) || req.reply}
                      </Typography>
                    </Box>

                    {/* Threaded Replies */}
                    {!!req.replies?.length && (
                      <Box
                        sx={{
                          // pl: { xs: 0, sm: 7 },
                          mb: 2
                        }}
                      >
                        {req.replies.map((reply, idx) => (
                          <Stack
                            key={idx}
                            direction="column"
                            spacing={1.2}
                            sx={{
                              p: 2,
                              mb: 1.5,
                              bgcolor: !reply.userId?._id
                                ? styles.background
                                : "transparent",
                              borderRadius: 2,
                              border: !reply.userId?._id
                                ? `1px solid ${styles.trackerBackground}`
                                : "none",
                              wordBreak: "break-word"
                            }}
                          >
                            {/* ROW 1 — Avatar + Name + Date */}
                            <Stack
                              direction="row"
                              spacing={2}
                              alignItems="center"
                            >
                              {/* Avatar */}
                              {reply.userId?.profilePic ? (
                                <Avatar
                                  src={reply.userId.profilePic}
                                  sx={{ width: 32, height: 32 }}
                                />
                              ) : (
                                <Avatar sx={{ width: 32, height: 32 }}>
                                  {getInitials(
                                    reply.userId?.displayName ||
                                    reply.userId?.fullName
                                  )}
                                </Avatar>
                              )}

                              {/* Name + Date stacked vertically (better mobile handling) */}
                              <Stack
                                flex={1}
                                sx={{
                                  ml: "8px !important"
                                }}
                                direction="row"
                                justifyContent="space-between"
                              >
                                <Typography
                                  variant="caption"
                                  onClick={() => {
                                    if (reply.userId?._id) {
                                      setIsViewingUserMeals({
                                        createdByUser: reply.userId
                                      });
                                      navigate(`/meals?isFromQA=true`);
                                    }
                                  }}
                                  sx={{
                                    fontWeight: 600,
                                    color: styles.primaryColor,
                                    pr: 1,
                                    flexShrink: 1,
                                    maxWidth: "70%",
                                    cursor: "pointer"
                                  }}
                                >
                                  {reply.userId?.displayName ||
                                    reply.userId?.fullName ||
                                    "EC's Answer"}
                                </Typography>

                                <Typography
                                  variant="caption"
                                  sx={{ color: styles.modLabel }}
                                >
                                  {dayjs(reply.createdAt).format(
                                    "MMM DD, YYYY"
                                  )}
                                </Typography>
                              </Stack>
                            </Stack>

                            {/* ROW 2 — Full Width Message */}
                            <Box sx={{ pl: { xs: 1.5, md: 4 } }}>
                              <Typography
                                variant="body2"
                                sx={{
                                  mt: { xs: 0.3, md: 0 },
                                  color: styles.color,
                                  whiteSpace: "pre-wrap",
                                  overflowWrap: "break-word"
                                }}
                              >
                                {convertTextToOnlyLink(reply.message) ||
                                  reply.message}
                              </Typography>
                            </Box>
                          </Stack>
                        ))}
                      </Box>
                    )}

                    {/* Reply Box */}
                    {!currentUser?.isSubscriptionActive &&
                      !currentUser?.isAdmin &&
                      ["Gym", "Affiliate", "Individual"].includes(
                        currentUser?.userType
                      ) ? (
                      <></>
                    ) : (
                      <Box sx={{ mt: 2 }}>
                        <Stack
                          direction="row"
                          spacing={2}
                          alignItems="flex-start"
                        >
                          {currentUser?.profilePic ? (
                            <Avatar
                              src={imgSrc}
                              sx={{ width: 36, height: 36 }}
                            />
                          ) : (
                            <Avatar sx={{ width: 36, height: 36 }}>
                              {getInitials(
                                currentUser?.displayName ||
                                currentUser?.fullName
                              )}
                            </Avatar>
                          )}

                          <Box flex={1}>
                            <TextField
                              fullWidth
                              multiline
                              rows={3}
                              placeholder="Add a reply..."
                              value={replyTexts[req._id] || ""}
                              onChange={(e) =>
                                handleReplyChange(req._id, e.target.value)
                              }
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: 1,
                                  color: styles.color
                                },
                                "& textarea": {
                                  whiteSpace: "pre-wrap",
                                  overflowWrap: "break-word"
                                }
                              }}
                            />

                            <Box
                              display="flex"
                              justifyContent="flex-end"
                              mt={1}
                            >
                              <Button
                                variant="contained"
                                size="small"
                                onClick={() => handleSubmitReply(req._id)}
                                disabled={
                                  submitting[req._id] ||
                                  !replyTexts[req._id]?.trim()
                                }
                                startIcon={
                                  submitting[req._id] ? (
                                    <CircularProgress size={16} />
                                  ) : (
                                    <Send />
                                  )
                                }
                                sx={{
                                  bgcolor: styles.primaryColor,
                                  color: "#FFF",
                                  textTransform: "none",
                                  "&:hover": { opacity: 0.9 }
                                }}
                              >
                                {submitting[req._id] ? "Sending..." : "Reply"}
                              </Button>
                            </Box>
                          </Box>
                        </Stack>
                      </Box>
                    )}
                  </Collapse>
                </Paper>
              </Fade>
            ))}
          </Box>

          {/* Pagination */}
          <Box
            display="flex"
            justifyContent="center"
            mt={4}
            sx={{
              "& .MuiPagination-ul li button": { color: styles.color },
              "& .Mui-selected": {
                backgroundColor: styles.primaryColor + " !important",
                color: "#fff !important"
              }
            }}
          >
            <Pagination
              count={Math.ceil(totalRequests / rowsPerPage)}
              page={page}
              shape="rounded"
              siblingCount={1}
              boundaryCount={1}
              onChange={(e, val) => setPage(val)}
            />
          </Box>
        </>
      )}
    </Box>
  );
};

export default PastRequestsTable;
